<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  content,
  hasTitle,
  hasContent,
  hasAnyContent,
} = useBlockSafe(props.data);
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      backgroundColor === 'black' ? 'white' : 'black',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="two-columns">
        <h2
          v-if="hasTitle"
          class="mb-0 two-columns__title h2"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>
        <div v-if="hasContent" class="two-columns__content">
          <div
            v-for="(row, rowIndex) in content"
            :key="rowIndex"
            class="two-columns__row"
            :class="[
              row?.image?.position
                ? `two-columns__row--image-${row.image.position}`
                : '',
            ]"
          >
            <div
              v-if="row?.text"
              class="two-columns__text"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              v-html="row.text"
            ></div>
            <div
              v-if="row?.image?.src"
              class="two-columns__media"
              data-aos="zoom-in"
              data-aos-easing="ease-out-quad"
            >
              <NuxtImg
                v-if="row?.image?.src"
                :src="row.image.src"
                :alt="row.image.alt"
                class="two-columns__image"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.two-columns {
  $parent: &;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(sm) {
    gap: 64px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 64);
  }
  &__title {
    margin: 0;
    @include media-breakpoint-up(md) {
      text-align: center;
      max-width: 80%;
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    row-gap: 32px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(row-gap, 32);
    }
  }
  &__row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      column-gap: 12px;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(column-gap, 12);
      @include responsive-spacing(row-gap, 32);
    }
    &--image-left {
      #{$parent} {
        @include media-breakpoint-up(md) {
          &__image {
            order: 1;
          }
          &__text {
            order: 2;
            margin-left: auto;
          }
        }
      }
    }
  }
  &__media {
    width: 100%;
    @include media-breakpoint-up(md) {
      margin-top: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(margin-top, 16);
    }
  }
  &__image {
    border-radius: 4px;
    height: 100%;
    object-fit: cover;
    width: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
    }
  }
  &__text {
    @include body-3-regular;
    @include media-breakpoint-up(sm) {
      @include body-1-regular();
      max-width: 74%;
    }
    @include media-breakpoint-up(md) {
      max-width: 83%;
    }
  }
}
</style>
